import React from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Target, Euro, Building2, ChevronRight, FileText,
  Phone, PhoneCall, UserPlus, Calendar, Percent, Play, Pause, AlertTriangle,
  Receipt, Banknote,
} from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

const iconMap = {
  users: Users,
  target: Target,
  euro: Euro,
  building: Building2,
  phone: Phone,
  'phone-call': PhoneCall,
  'file-text': FileText,
  'user-plus': UserPlus,
  calendar: Calendar,
  percent: Percent,
  play: Play,
  pause: Pause,
  receipt: Receipt,
  banknote: Banknote,
};

const tintMap = {
  // Direzione V4 — Entrepreneur Control Room
  da_fatturare: { bg: '#FFF1E6', text: 'text-[#FF7819]' },
  da_incassare: { bg: '#EAF7EE', text: 'text-[#1E9E5A]' },
  // Direzione V2 (legacy ids)
  new_leads: { bg: '#E5F4FF', text: 'text-[#1D6FD1]' },
  active_opportunities: { bg: '#FFF1E6', text: 'text-[#FF7819]' },
  pipeline_value: { bg: '#EAF7EE', text: 'text-[#1E9E5A]' },
  active_projects: { bg: '#E5F7ED', text: 'text-[#008A3D]' },
  // PM V2 · Call
  nuovi_da_contattare: { bg: '#E5F4FF', text: 'text-[#1D6FD1]' },
  da_richiamare: { bg: '#FFF4E0', text: 'text-[#C77700]' },
  da_assegnare: { bg: '#FFF1E6', text: 'text-[#FF7819]' },
  appuntamenti_oggi: { bg: '#E3F6F5', text: 'text-[#0E8F86]' },
  // Follow-up V2/V3 (Tecnico + PM)
  nuovi_assegnati: { bg: '#E5F4FF', text: 'text-[#1D6FD1]' },
  da_ricontattare: { bg: '#FFF4E0', text: 'text-[#C77700]' },
  da_seguire: { bg: '#FFF4E0', text: 'text-[#C77700]' },
  attese_scadute: { bg: '#FDECEC', text: 'text-[#C0392B]' },
  in_preventivazione: { bg: '#ECEBFB', text: 'text-[#5B54D6]' },
  in_trattativa: { bg: '#FFF1E6', text: 'text-[#FF7819]' },
  // PM V2 · Progetti
  cantieri_attivi: { bg: '#E5F7ED', text: 'text-[#008A3D]' },
  sal_medio: { bg: '#ECEBFB', text: 'text-[#5B54D6]' },
  da_avviare: { bg: '#E5F4FF', text: 'text-[#1D6FD1]' },
  sospesi: { bg: '#FDECEC', text: 'text-[#C0392B]' },
};

export const DashboardKpiCard = ({ kpi, testIdPrefix = 'direzione-kpi', onAction, onWarningAction }) => {
  const Icon = iconMap[kpi.icon] || FileText;
  const tint = tintMap[kpi.id] || { bg: '#F0F0F0', text: 'text-[#3C3C3C]' };
  const isMoney = kpi.value !== undefined && kpi.value !== null;
  const display = kpi.display != null
    ? String(kpi.display)
    : (isMoney ? formatCurrency(kpi.value) : String(kpi.count ?? 0));
  const len = display.length;
  const sizeClass = len > 13 ? 'text-base sm:text-xl' : len > 10 ? 'text-lg sm:text-2xl' : 'text-2xl';

  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: tint.bg }}>
          <Icon className={`w-5 h-5 ${tint.text}`} />
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF7819] transition-colors" />
      </div>
      <div className="mt-4">
        <p className="text-sm text-gray-500 font-medium">{kpi.title}</p>
        <p className={`${sizeClass} font-bold text-[#3C3C3C] mt-1 whitespace-nowrap`} data-testid={`${testIdPrefix}-${kpi.id}-value`} title={isMoney ? display : undefined}>
          {display}
        </p>
        {kpi.subtitle && <p className="text-xs text-gray-400 mt-1" data-testid={`${testIdPrefix}-${kpi.id}-subtitle`}>{kpi.subtitle}</p>}
        {kpi.microcopy && !kpi.subtitle && <p className="text-xs text-gray-400 mt-1">{kpi.microcopy}</p>}
        {kpi.warning_label ? (
          <button
            type="button"
            data-testid={`${testIdPrefix}-${kpi.id}-warning`}
            onClick={onWarningAction ? (e) => { e.stopPropagation(); onWarningAction(); } : undefined}
            onKeyDown={onWarningAction ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onWarningAction(); } } : undefined}
            className={`mt-2 flex items-center gap-1 text-xs font-medium text-[#C0392B] text-left ${onWarningAction ? 'hover:underline cursor-pointer' : 'cursor-default'}`}
          >
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {kpi.warning_label}
          </button>
        ) : null}
      </div>
    </>
  );

  const cls = "group block bg-white rounded-xl border border-[#E4E4E4] p-5 transition-all duration-200 hover:shadow-md hover:border-[#D4D4D4] no-underline text-inherit";

  if (onAction) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onAction}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAction(); } }}
        title={kpi.tooltip || ''}
        data-testid={`${testIdPrefix}-${kpi.id}`}
        className={`${cls} cursor-pointer text-left w-full focus:outline-none focus:ring-2 focus:ring-[#FF7819]/40`}
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={kpi.link || '#'}
      title={kpi.tooltip || ''}
      data-testid={`${testIdPrefix}-${kpi.id}`}
      className={cls}
    >
      {inner}
    </Link>
  );
};
